<template>
  <div
    class="main-banner__item"
    :class="contentPositionClass(contentPosition)"
    :style="bannerStyle"
  >
    <video
      v-if="video"
      class="main-banner__image"
      :src="getImagePath(video)"
      playsinline
      autoplay
      loop
      muted
    />
    <image-component
      v-else
      class="main-banner__image"
      loading="lazy"
      :path="image"
    />
    <div v-if="hasContent" class="main-banner__content">
      <h2 v-if="mainTitle" class="main-banner__title">{{ mainTitle }}</h2>
      <h4 v-if="secondTitle" class="main-banner__subtitle">{{ secondTitle }}</h4>
      <app-link
        v-if="btnTitle"
        :to="link"
        class="main-banner__button"
        :style="buttonStyle"
      >
        {{ btnTitle }}
      </app-link>
    </div>
  </div>
</template>

<script>
import ImageComponent from '@/components/base-components/ImageComponent';
import AppLink from '@/components/base-components/AppLink.vue';
import {getHexAlpha} from '@/utils/colorScheme';

export default {
  name: 'SlideItem',
  components: {
    ImageComponent,
    AppLink,
  },
  props: {
    contentPosition: {
      type: String,
      default: 'center-left',
    },
    textColor: {
      type: String,
      default: '',
    },
    backgroundColor: {
      type: String,
      default: '',
    },
    opacity: {
      type: Number,
      default: 1,
    },
    textAlign: {
      type: String,
      default: 'left',
    },
    video: {
      type: String,
      default: '',
    },
    image: {
      type: String,
      default: '',
    },
    mainTitle: {
      type: String,
      default: '',
    },
    secondTitle: {
      type: String,
      default: '',
    },
    btnTitle: {
      type: String,
      default: '',
    },
    link: {
      type: String,
      default: '',
    },
    btnColor: {
      type: String,
      default: 'transparent',
    },
    btnTextColor: {
      type: String,
      default: '',
    },
  },
  computed: {
    hasContent() {
      return this.mainTitle || this.secondTitle || this.btnTitle;
    },
    bannerStyle() {
      return {
        '--text-color': this.textColor,
        '--background': this.bannerBackground(this.opacity, this.backgroundColor),
        'textAlign': this.textAlign,
      };
    },
    buttonStyle() {
      return {
        'background-color': this.btnColor,
        '--text': this.btnTextColor,
      };
    },
  },
  methods: {
    bannerBackground(alpha, color) {
      return `${color}${getHexAlpha(alpha * 100)}`;
    },
    contentPositionClass(position) {
      return position || 'center-left';
    },
  },
};
</script>

<style lang="scss" scoped>
@import "src/assets/css/colors";
.main-banner__item {
  max-width: 100vw;
  position: relative;
  background: var(--background);
  color: var(--text-color);
  font-style: normal;
  text-align: left;
}

.main-banner__image {
  object-fit: fill;
  width: 100vw;
  float: right;
}

.main-banner__content {
  position: absolute;
  width: 45%;
  max-height: 100%;
  overflow-y: auto;
  padding: 1rem 10% 1rem 2rem;
  clip-path: polygon(0 0, 90% 0, 100% 100%, 0 100%);
  backdrop-filter: blur(.15rem);
  display: flex;
  flex-direction: column;
  gap: .5rem;
  justify-content: center;
}

.main-banner__title {
  font-weight: 700;
  font-size: clamp(.9rem, 2.5vw, 2.125rem)
}

.main-banner__subtitle {
  font-weight: 400;
  font-size: clamp(.8rem, 2vw, 1.25rem)
}

.main-banner__button {
  background: transparent;
  display: inline-block;
  padding: .75rem 1.25rem;
  color: var(--text);
  transition: all .2s linear;
  position: relative;
  align-self: center;

  &:before {
    width: 10%;
    height: 25%;
    border-top: 1px solid var(--text);
    border-left: 1px solid var(--text);
    position: absolute;
    content: '';
    display: block;
    top: 0;
    left: 0;
    transition: all .15s linear;
  };

  &:after {
    position: absolute;
    content: '';
    display: block;
    width: 10%;
    height: 25%;
    border-bottom: 1px solid var(--text);
    border-right: 1px solid var(--text);
    bottom: 0;
    right: 0;
    transition: all .15s linear;
  }

  &:hover {
    text-decoration: none;
    color: var(--text);

    &:before,
    &:after {
      width: 100%;
      height: 100%;
    }
  }
}
.top-left{
  .main-banner__content{
    top: 0;
    left: 0;
    transform: none;
    background: linear-gradient(65deg, var(--background) 80%, transparent 90%);
  }
}
.center-left{
  .main-banner__content{
    top: 50%;
    left: 0;
    transform: translateY(-50%);
    background: linear-gradient(65deg, var(--background) 80%, transparent 90%);
  }
}
.bottom-left{
  .main-banner__content{
    top: initial;
    bottom: 0;
    left: 0;
    transform: none;
    background: linear-gradient(65deg, var(--background) 80%, transparent 90%);
  }
}
.top-right{
  .main-banner__content{
    top: 0;
    right: 0;
    background: linear-gradient(290deg, var(--background) 80%,  transparent 90%);
    clip-path: polygon(10% 0, 100% 0, 100% 100%, 0 100%);
    padding: 1rem 2rem 1rem 10%;
  }
}
.center-right{
  .main-banner__content{
    top: 50%;
    transform: translateY(-50%);
    right: 0;
    background: linear-gradient(290deg, var(--background) 80%,  transparent 90%);
    clip-path: polygon(10% 0, 100% 0, 100% 100%, 0 100%);
    padding: 1rem 2rem 1rem 10%;
  }
}
.bottom-right{
  .main-banner__content{
    bottom: 0;
    right: 0;
    background: linear-gradient(290deg, var(--background) 80%,  transparent 90%);
    clip-path: polygon(10% 0, 100% 0, 100% 100%, 0 100%);
    padding: 1rem 2rem 1rem 10%;
  }
}
.center{
  .main-banner__content{
    top: 50%;
    transform: translateY(-50%) translateX(-50%);
    left: 50%;
    background: linear-gradient(
      105deg,
      transparent 10%,
      var(--background) 20%,
      var(--background) 80%,
      transparent 90%
    );
    clip-path: polygon(10% 0, 100% 0, 90% 100%, 0 100%);
    padding: 1rem 10% 1rem 10%;
  }
}

@include media-max(md) {
  .main-banner__button {
    padding: .25rem .5rem;
    font-weight: 500;
    font-size: .8rem;
    align-self: flex-start;
  }
}
@include media-max(xs) {
  .main-banner__item{
    display: flex;
    flex-direction: column;
  }
  .main-banner__image {
    mask-image: linear-gradient(to bottom, black calc(100% - 2rem), transparent);
  }
  .main-banner__item{
    .main-banner__content{
      position: static;
      transform: none;
      width: 100%;
      background: transparent;
      flex-grow: 1;
      padding: 1rem;
      clip-path: none;
    }
  }
}
</style>
